import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, Typography, CircularProgress } from '@mui/material';
import { apiService, extractApiErrorMessage } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import type { AgentMessageResponse } from '../types/agent';
import { runAgentSingleRequestViaClientProxy } from '../utils/agentRequestExecution';
import {
  fetchRequestsForAgentCollectionScope,
  mapCollectionsForAgentMenus,
  resolveAgentExecuteEnvironment,
  type AgentCollectionOption,
} from '../utils/agentCollectionScope';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { AgentInlineSelect } from './ai-agent-ui/index';

export type AgentWidgetExecuteRequestFormProps = {
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  defaultCollectionId?: number | '';
  defaultRequestId?: number | '';
  defaultEnvironmentId?: number | '';
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetExecuteRequestForm: React.FC<AgentWidgetExecuteRequestFormProps> = ({
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  defaultCollectionId = '',
  defaultRequestId = '',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const { setSelectedProject } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [collections, setCollections] = useState<AgentCollectionOption[]>([]);
  const [requests, setRequests] = useState<
    Array<{ id: number; name: string; environmentId: number | ''; environmentUrl: string }>
  >([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | ''>('');
  const [selectedRequestId, setSelectedRequestId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      if (defaultProjectId === '' || Number.isNaN(Number(defaultProjectId))) {
        return list;
      }
      const id = Number(defaultProjectId);
      if (list.some((p) => p.id === id)) return list;
      const name = defaultProjectName.trim() || `Project ${id}`;
      return [...list, { id, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const fetched = await apiService.getProjects();
      setAvailableProjects(mergeProjectOption(fetched.map((p) => ({ id: p.id, name: p.name }))));
    } catch {
      setAvailableProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [mergeProjectOption]);

  const loadRequestsForCollection = useCallback(
    async (
      projectId: number | '',
      collectionId: number | '',
      collectionRows: AgentCollectionOption[],
    ) => {
      if (projectId === '' || collectionId === '') {
        setRequests([]);
        setSelectedRequestId('');
        return;
      }
      try {
        const mapped = await fetchRequestsForAgentCollectionScope(
          Number(projectId),
          collectionId,
          collectionRows,
        );
        const options = mapped.map((r) => ({
          id: r.id,
          name: r.name,
          environmentId: r.environmentId,
          environmentUrl: r.environmentUrl,
        }));
        setRequests(options);
        setSelectedRequestId((prev) => {
          if (prev !== '' && options.some((r) => r.id === prev)) return prev;
          if (defaultRequestId !== '' && options.some((r) => r.id === defaultRequestId)) {
            return defaultRequestId;
          }
          return options[0]?.id ?? '';
        });
      } catch {
        setRequests([]);
        setSelectedRequestId('');
      }
    },
    [defaultRequestId],
  );

  const loadProjectDetails = useCallback(
    async (projectId: number | '') => {
      if (projectId === '') {
        setCollections([]);
        setRequests([]);
        setSelectedCollectionId('');
        setSelectedRequestId('');
        return;
      }
      setLoadingDetails(true);
      try {
        const colls = await apiService.getCollectionsByProject(Number(projectId));
        const mappedColls = mapCollectionsForAgentMenus(colls);
        setCollections(mappedColls);

        const collectionId: number | '' =
          defaultCollectionId !== '' && mappedColls.some((c) => c.id === defaultCollectionId)
            ? defaultCollectionId
            : mappedColls.length > 0
              ? mappedColls[0].id
              : '';
        setSelectedCollectionId(collectionId);

        let reqs: Array<{ id: number; name: string; environmentId: number | ''; environmentUrl: string }> =
          [];
        if (collectionId !== '') {
          const mapped = await fetchRequestsForAgentCollectionScope(
            Number(projectId),
            collectionId,
            mappedColls,
          );
          reqs = mapped.map((r) => ({
            id: r.id,
            name: r.name,
            environmentId: r.environmentId,
            environmentUrl: r.environmentUrl,
          }));
        }
        setRequests(reqs);

        const requestId: number | '' =
          defaultRequestId !== '' && reqs.some((r) => r.id === defaultRequestId)
            ? defaultRequestId
            : reqs.length > 0
              ? reqs[0].id
              : '';
        setSelectedRequestId(requestId);
      } catch {
        setCollections([]);
        setRequests([]);
      } finally {
        setLoadingDetails(false);
      }
    },
    [defaultCollectionId, defaultRequestId],
  );

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    if (defaultProjectId !== '' && availableProjects.some((p) => p.id === defaultProjectId)) {
      setSelectedProjectId(defaultProjectId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  useEffect(() => {
    if (selectedProjectId === '') return;
    void loadProjectDetails(selectedProjectId);
  }, [selectedProjectId, loadProjectDetails]);

  const handleExecute = async () => {
    if (!selectedCollectionId || !selectedRequestId) return;
    const listed = requests.find((r) => r.id === selectedRequestId);
    const requestName = listed?.name ?? `request #${selectedRequestId}`;
    let requestForEnv: {
      environmentId?: number | '' | null;
      environment?: { id?: number | '' | null; name?: string } | null;
      environmentUrl?: string | null;
    } | undefined = listed;
    try {
      requestForEnv = await apiService.getRequest(
        Number(selectedRequestId),
        selectedProjectId === '' ? undefined : Number(selectedProjectId),
      );
    } catch {
      /* use the collection request environment already loaded */
    }
    let environments: Array<{ id: number; url?: string; baseUrl?: string; name?: string }> = [];
    if (selectedProjectId !== '') {
      try {
        environments = await apiService.getEnvironmentsByProject(Number(selectedProjectId));
      } catch {
        environments = [];
      }
    }
    const { environmentId, environmentUrl } = resolveAgentExecuteEnvironment(
      requestForEnv,
      environments,
    );
    if (!environmentId) return;
    const envName =
      requestForEnv?.environment?.name ||
      environments.find((e) => e.id === environmentId)?.name ||
      environmentUrl;
    const userText = envName
      ? `Execute request "${requestName}" in environment "${envName}".`
      : `Execute request "${requestName}".`;
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await runAgentSingleRequestViaClientProxy(
        {
          projectId: selectedProjectId || undefined,
          collectionId: selectedCollectionId,
          requestId: selectedRequestId,
          environmentId,
          ...(environmentUrl ? { environmentUrl } : {}),
          requestName,
        },
        {
          chatId: chatId ?? undefined,
          message: userText,
        },
      );
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state!.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      try {
        sessionStorage.setItem('collectionsAgentEnvironmentId', String(environmentId));
      } catch {
        /* ignore */
      }
      await onAgentReply(res);
      // Close after any completed execute (pass or fail), same as AI Agent page.
      onClose();
    } catch (err: unknown) {
      const message =
        extractApiErrorMessage(err) ||
        (err instanceof Error ? err.message : 'Request execution failed.');
      const failure: AgentMessageResponse = {
        reply: message,
        error: message,
        actions: [],
      };
      await onAgentReply(failure);
      onClose();
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingProjects || loadingDetails;
  const fieldGapSx = { mb: 1, width: '100%' };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
        ...environmentPlaceholderSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Execute request
      </Typography>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-exec-req-project"
          label="Project"
          fullWidth
          value={selectedProjectId === '' ? '' : selectedProjectId}
          disabled={busy}
          emptyLabel="No projects found"
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-exec-req-collection"
          label="Collection"
          fullWidth
          value={selectedCollectionId === '' ? '' : selectedCollectionId}
          disabled={busy || !selectedProjectId}
          emptyLabel="No collections found"
          options={collections.map((collection) => ({
            value: collection.id,
            label: collection.label,
            indent: collection.depth,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            const collectionId = Number.isNaN(parsed) || raw === '' ? '' : parsed;
            setSelectedCollectionId(collectionId);
            void loadRequestsForCollection(selectedProjectId, collectionId, collections);
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-exec-req-request"
          label="Request"
          fullWidth
          value={selectedRequestId === '' ? '' : selectedRequestId}
          disabled={busy || !selectedCollectionId}
          emptyLabel="No requests found"
          options={requests.map((request) => ({
            value: request.id,
            label: request.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedRequestId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleExecute()}
          disabled={busy || !selectedCollectionId || !selectedRequestId}
          sx={{ textTransform: 'none' }}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : 'Execute'}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetExecuteRequestForm;
